import * as React from "react";
import { useState, useCallback } from "react";
import URI from "urijs";
import { Reports } from "@graylog/enterprise-api";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { MenuItem, DeleteMenuItem } from "components/bootstrap";
import ButtonToolbar from "components/bootstrap/ButtonToolbar";
import useSelectedEntities from "components/common/EntityDataTable/hooks/useSelectedEntities";
import UserNotification from "util/UserNotification";
import { HoverForHelp, ShareButton, IfPermitted } from "components/common";
import { reportingUrl } from "report/Constants";
import { MoreActions } from "components/common/EntityDataTable";
import ReportRoutes from "report/ReportRoutes";
import useTableFetchContext from "components/common/PaginatedEntityTable/useTableFetchContext";
import EntityShareModal from "components/permissions/EntityShareModal";
import usePluggableEntitySharedActions from "hooks/usePluggableEntitySharedActions";
import useScopePermissions from "hooks/useScopePermissions";
import useSendIlluminateReportDownloadedTelemetry from "report/hooks/useSendIlluminateReportDownloadedTelemetry";
const downloadUrl = (reportId, reportTitle) => new URI(reportingUrl(encodeURI(`${reportId}/generate/${reportTitle}`))).toString();
const _reportHasRecipients = (delivery) => delivery && (Array.isArray(delivery.user_receivers) && delivery.user_receivers.length > 0 || Array.isArray(delivery.email_receivers) && delivery.email_receivers.length > 0);
const SendReportMenuItem = ({
  reportDelivery,
  reportId,
  reportTitle
}) => {
  const sendTelemetry = useSendTelemetry("report-form");
  const reportHasRecipients = _reportHasRecipients(reportDelivery);
  const isDisabled = !reportHasRecipients;
  const onSendReport = async () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.REPORT.SENT, {
      app_pathname: "report"
    });
    await Reports.sendReport(reportId).then(() => {
      UserNotification.success(
        "The Report is being generated and will be send to all recipients shortly, check your inbox in a few minutes.",
        "Report delivery scheduled"
      );
    }).catch(() => {
      UserNotification.error(
        `Sending Report "${reportTitle}" email failed. Please check your server logs for more information.`,
        "Could not send Report"
      );
    });
  };
  return /* @__PURE__ */ React.createElement(MenuItem, { disabled: isDisabled, onSelect: onSendReport }, "Send report now", isDisabled && /* @__PURE__ */ React.createElement(HoverForHelp, { trigger: ["hover", "click"], placement: "left", displayLeftMargin: true }, "Sending report disabled, add recipients first"));
};
const DeleteReportAction = ({ reportId, reportTitle }) => {
  const { deselectEntity } = useSelectedEntities();
  const { refetch } = useTableFetchContext();
  const sendTelemetry = useSendTelemetry("reports-overview");
  const onDeleteReport = async () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.REPORT.DELETED, {
      app_pathname: "report"
    });
    if (window.confirm(`Do you really want to delete report "${reportTitle}"? This action cannot be undone.`)) {
      await Reports.remove(reportId).then(() => {
        deselectEntity(reportId);
        UserNotification.success(`Report "${reportTitle}" was deleted successfully.`, "Success");
        refetch();
      }).catch((error) => {
        UserNotification.error(`Deleting Report "${reportTitle}" failed with status: ${error}`);
      });
    }
  };
  return /* @__PURE__ */ React.createElement(DeleteMenuItem, { onSelect: onDeleteReport });
};
const ReportActions = ({ report }) => {
  const [showReportShareModal, setShowReportShareModal] = useState(false);
  const { actions: pluggableActions, actionModals: pluggableActionModals } = usePluggableEntitySharedActions(report, "report");
  const moreActions = [pluggableActions.length ? pluggableActions : null].filter(Boolean);
  const { scopePermissions } = useScopePermissions(report);
  const scopeDeletable = scopePermissions.is_deletable !== false;
  const toggleShareModalOpen = useCallback(() => {
    setShowReportShareModal((cur) => !cur);
  }, [setShowReportShareModal]);
  const sendTelemetry = useSendTelemetry("reports-overview");
  const sendIlluminateReportDownloaded = useSendIlluminateReportDownloadedTelemetry("reports-overview");
  const onDownload = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.REPORT.DOWNLOAD, {
      app_pathname: "report"
    });
    sendIlluminateReportDownloaded(report);
  };
  return /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(ShareButton, { entityType: "report", entityId: report.id, onClick: toggleShareModalOpen, bsSize: "xs" }), /* @__PURE__ */ React.createElement(MoreActions, null, /* @__PURE__ */ React.createElement(MenuItem, { href: ReportRoutes.contents(report.id) }, "Edit"), /* @__PURE__ */ React.createElement(MenuItem, { href: ReportRoutes.archive(report.id) }, "Report archive"), /* @__PURE__ */ React.createElement(MenuItem, { href: downloadUrl(report.id, report.title), target: "_blank", onClick: onDownload }, "Download report now"), /* @__PURE__ */ React.createElement(SendReportMenuItem, { reportDelivery: report.delivery, reportTitle: report.title, reportId: report.id }), moreActions, scopeDeletable && /* @__PURE__ */ React.createElement(IfPermitted, { permissions: `report:delete:${report.id}` }, /* @__PURE__ */ React.createElement(MenuItem, { divider: true }), /* @__PURE__ */ React.createElement(DeleteReportAction, { reportId: report.id, reportTitle: report.title }))), showReportShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: report.id,
      entityType: "report",
      entityTitle: report.title,
      description: "Search for a User or Team to add as collaborator on this report.",
      onClose: toggleShareModalOpen
    }
  ), pluggableActionModals);
};
export default ReportActions;
